export const translations = {
  en: {
    nav: {
      links: [
        'Finance',
        'Credit',
        'AI',
        'Software',
        'Rewards',
        'Learn',
        'About',
      ],
      startExploring: 'Start Exploring',
    },
    hero: {
      badge: 'Decision Intelligence Platform',
      title: 'Every important decision starts with confidence.',
      subtitle:
        'Blinkblis helps people make smarter decisions about money, credit, AI and digital tools through trusted research, comparisons and practical guidance.',
      startExploring: 'Start Exploring',
      seeHowItWorks: 'See How It Works',
      network: 'Decision Intelligence Network',
    },
    ecosystem: {
      title: 'Explore the Ecosystem',
      subtitle:
        'Comprehensive insights across six core domains, designed to elevate your personal and professional capabilities.',
      cards: {
        money: {
          title: 'Finance',
          desc: 'Wealth management and banking strategies.',
        },
        credit: {
          title: 'Credit',
          desc: 'Optimize your credit profile and rewards.',
        },
        ai: {
          title: 'AI',
          desc: 'Leverage artificial intelligence for productivity.',
        },
        software: {
          title: 'Software',
          desc: 'Premium tools for modern workflows.',
        },
        rewards: {
          title: 'Rewards',
          desc: 'Maximize points, miles, and cashback.',
        },
        business: {
          title: 'Business',
          desc: 'Enterprise solutions and corporate strategy.',
        },
      },
    },
    trust: {
      title: 'Why Trust Blinkblis.',
      subtitle:
        'We build decision frameworks, not marketing funnels. Our intelligence is rooted in rigorous analysis and absolute transparency.',
      pillars: {
        editorial: {
          title: 'Editorial Standards',
          desc: 'Uncompromising integrity in every recommendation.',
        },
        research: {
          title: 'Independent Research',
          desc: 'Data-driven analysis, free from external influence.',
        },
        humanai: {
          title: 'AI-Assisted + Human Reviewed',
          desc: 'The scale of AI, verified by expert analysts.',
        },
        updates: {
          title: 'Weekly Updates',
          desc: 'Continuous monitoring of shifting market dynamics.',
        },
      },
    },
    featured: {
      title: 'Featured Decisions',
      subtitle:
        'Editorial insights engineered to help you make the right choice, faster.',
      viewAll: 'View all decisions',
      labels: {
        confidence: 'Confidence Score',
        recommendation: 'Recommendation',
      },
      cards: [
        {
          category: 'Money',
          title: 'Should you switch banks?',
          readTime: '5 min read',
          confidence: '98%',
          recommendation: 'Yes, if paying fees.',
        },
        {
          category: 'Software',
          title: 'Best Budgeting Apps',
          readTime: '8 min read',
          confidence: '95%',
          recommendation: 'YNAB for strict budgeting.',
        },
        {
          category: 'Credit',
          title: 'Best Secured Credit Cards',
          readTime: '6 min read',
          confidence: '92%',
          recommendation: 'Discover it® Secured.',
        },
        {
          category: 'AI',
          title: 'Best AI for Students',
          readTime: '7 min read',
          confidence: '96%',
          recommendation: 'Perplexity & ChatGPT Plus.',
        },
        {
          category: 'Business',
          title: 'Best Business Checking Accounts',
          readTime: '10 min read',
          confidence: '94%',
          recommendation: 'Novo or Bluevine.',
        },
      ],
    },
    spotlights: {
      finance: {
        badge: 'Ecosystem Spotlight',
        title: 'Personal Finance, Elevated.',
        desc: 'Navigate the complexities of modern wealth management. From high-yield strategies to optimizing your banking stack, we provide the blueprint for financial clarity.',
        cta: 'Explore Finance',
      },
      ai: {
        badge: 'Future Expansion',
        title: 'The AI Advantage.',
        desc: 'Artificial Intelligence is reshaping productivity. Discover the tools, frameworks, and strategies to integrate AI into your daily workflow and maintain a competitive edge.',
        cta: 'Explore AI',
      },
    },
    youtube: {
      title: 'Visual Intelligence',
      subtitle:
        'Deep-dive analysis and educational content, delivered in cinematic quality.',
      badge: 'Featured Intelligence',
      videoTitle: 'The Architecture of Modern Wealth',
      videoDesc:
        'A deep dive into the systems and frameworks used by high-net-worth individuals to structure their financial lives.',
      cta: 'Watch on YouTube',
    },
    newsletter: {
      title: 'Weekly Decision Intelligence.',
      subtitle:
        'One email each week with practical insights, better tools and smarter recommendations.',
      placeholder: 'Enter your email address',
      label: 'Email address',
      cta: 'Subscribe',
      success: "Thanks! We'll be in touch soon.",
      disclaimer:
        'By subscribing, you agree to our Privacy Policy and Terms of Service.',
    },
    footer: {
      desc: 'The Decision Intelligence Platform empowering you to navigate financial and digital landscapes with absolute clarity.',
      platform: 'Platform',
      company: 'Company',
      legal: 'Legal',
      links: {
        platform: ['Finance', 'Credit', 'AI', 'Software', 'Rewards'],
        company: ['About', 'Careers', 'Contact', 'Press'],
        legal: [
          'Editorial Standards',
          'Methodology',
          'Affiliate Disclosure',
          'Privacy',
          'Terms',
          'Contact',
        ],
      },
      rights: 'Blinkblis Inc. All rights reserved.',
    },
  },
  es: {
    nav: {
      links: [
        'Finanzas',
        'Crédito',
        'IA',
        'Software',
        'Recompensas',
        'Aprender',
        'Nosotros',
      ],
      startExploring: 'Comenzar a Explorar',
    },
    hero: {
      badge: 'Plataforma de Inteligencia de Decisiones',
      title: 'Toda decisión importante comienza con confianza.',
      subtitle:
        'Blinkblis ayuda a las personas a tomar decisiones más inteligentes sobre dinero, crédito, IA y herramientas digitales mediante investigación confiable, comparaciones y guías prácticas.',
      startExploring: 'Comenzar a Explorar',
      seeHowItWorks: 'Cómo Funciona',
      network: 'Red de Inteligencia de Decisiones',
    },
    ecosystem: {
      title: 'Explora el Ecosistema',
      subtitle:
        'Perspectivas integrales en seis dominios principales, diseñadas para elevar tus capacidades personales y profesionales.',
      cards: {
        money: {
          title: 'Finanzas',
          desc: 'Gestión patrimonial y estrategias bancarias.',
        },
        credit: {
          title: 'Crédito',
          desc: 'Optimiza tu perfil crediticio y recompensas.',
        },
        ai: {
          title: 'IA',
          desc: 'Aprovecha la inteligencia artificial para la productividad.',
        },
        software: {
          title: 'Software',
          desc: 'Herramientas premium para flujos de trabajo modernos.',
        },
        rewards: {
          title: 'Recompensas',
          desc: 'Maximiza puntos, millas y reembolsos.',
        },
        business: {
          title: 'Negocios',
          desc: 'Soluciones empresariales y estrategia corporativa.',
        },
      },
    },
    trust: {
      title: 'Por qué confiar en Blinkblis.',
      subtitle:
        'Construimos marcos de decisión, no embudos de marketing. Nuestra inteligencia se basa en análisis riguroso y transparencia absoluta.',
      pillars: {
        editorial: {
          title: 'Estándares Editoriales',
          desc: 'Integridad intransigente en cada recomendación.',
        },
        research: {
          title: 'Investigación Independiente',
          desc: 'Análisis basado en datos, libre de influencias externas.',
        },
        humanai: {
          title: 'Asistido por IA + Revisado por Humanos',
          desc: 'La escala de la IA, verificada por analistas expertos.',
        },
        updates: {
          title: 'Actualizaciones Semanales',
          desc: 'Monitoreo continuo de las dinámicas cambiantes del mercado.',
        },
      },
    },
    featured: {
      title: 'Decisiones Destacadas',
      subtitle:
        'Perspectivas editoriales diseñadas para ayudarte a tomar la decisión correcta, más rápido.',
      viewAll: 'Ver todas las decisiones',
      labels: {
        confidence: 'Nivel de Confianza',
        recommendation: 'Recomendación',
      },
      cards: [
        {
          category: 'Dinero',
          title: '¿Deberías cambiar de banco?',
          readTime: 'Lectura de 5 min',
          confidence: '98%',
          recommendation: 'Sí, si pagas comisiones.',
        },
        {
          category: 'Software',
          title: 'Mejores Apps de Presupuesto',
          readTime: 'Lectura de 8 min',
          confidence: '95%',
          recommendation: 'YNAB para presupuestos estrictos.',
        },
        {
          category: 'Crédito',
          title: 'Mejores Tarjetas Garantizadas',
          readTime: 'Lectura de 6 min',
          confidence: '92%',
          recommendation: 'Discover it® Secured.',
        },
        {
          category: 'IA',
          title: 'Mejor IA para Estudiantes',
          readTime: 'Lectura de 7 min',
          confidence: '96%',
          recommendation: 'Perplexity y ChatGPT Plus.',
        },
        {
          category: 'Negocios',
          title: 'Mejores Cuentas Comerciales',
          readTime: 'Lectura de 10 min',
          confidence: '94%',
          recommendation: 'Novo o Bluevine.',
        },
      ],
    },
    spotlights: {
      finance: {
        badge: 'Destacado del Ecosistema',
        title: 'Finanzas Personales, Elevadas.',
        desc: 'Navega las complejidades de la gestión patrimonial moderna. Desde estrategias de alto rendimiento hasta la optimización de tu pila bancaria, proporcionamos el plan para la claridad financiera.',
        cta: 'Explorar Finanzas',
      },
      ai: {
        badge: 'Expansión Futura',
        title: 'La Ventaja de la IA.',
        desc: 'La Inteligencia Artificial está remodelando la productividad. Descubre las herramientas, marcos y estrategias para integrar la IA en tu flujo de trabajo diario y mantener una ventaja competitiva.',
        cta: 'Explorar IA',
      },
    },
    youtube: {
      title: 'Inteligencia Visual',
      subtitle:
        'Análisis profundo y contenido educativo, entregado en calidad cinematográfica.',
      badge: 'Inteligencia Destacada',
      videoTitle: 'La Arquitectura de la Riqueza Moderna',
      videoDesc:
        'Un análisis profundo de los sistemas y marcos utilizados por personas de alto patrimonio para estructurar sus vidas financieras.',
      cta: 'Ver en YouTube',
    },
    newsletter: {
      title: 'Inteligencia de Decisiones Semanal.',
      subtitle:
        'Un correo cada semana con perspectivas prácticas, mejores herramientas y recomendaciones más inteligentes.',
      placeholder: 'Ingresa tu correo electrónico',
      label: 'Correo electrónico',
      cta: 'Suscribirse',
      success: '¡Gracias! Pronto nos pondremos en contacto.',
      disclaimer:
        'Al suscribirte, aceptas nuestra Política de Privacidad y Términos de Servicio.',
    },
    footer: {
      desc: 'La Plataforma de Inteligencia de Decisiones que te empodera para navegar paisajes financieros y digitales con absoluta claridad.',
      platform: 'Plataforma',
      company: 'Compañía',
      legal: 'Legal',
      links: {
        platform: ['Finanzas', 'Crédito', 'IA', 'Software', 'Recompensas'],
        company: ['Nosotros', 'Empleos', 'Contacto', 'Prensa'],
        legal: [
          'Estándares Editoriales',
          'Metodología',
          'Divulgación de Afiliados',
          'Privacidad',
          'Términos',
          'Contacto',
        ],
      },
      rights: 'Blinkblis Inc. Todos los derechos reservados.',
    },
  },
}
